import React, { useEffect, useState, forwardRef, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, Download, Search, AlertCircle, Trash2, MessageSquare } from 'lucide-react';
import api from '../../services/api';
import commonStyles from '../../styles/common.module.css';
import styles from './ApprovalDetailPage.module.css';
import { formatDateTime } from '../../utils/dateUtils';
import Loading from '../Loading';
import CreateClientTicketPanel from '../ClientTickets/CreateClientTicketPanel';
import { shouldSendToTelegram } from '../../utils/telegram';

const getLogTimestamp = (value) => {
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const ApprovalDetailPage = forwardRef(({ repairRequestId: propRepairRequestId }, ref) => {
  const { repairRequestId: paramRepairRequestId } = useParams();
  const repairRequestId = propRepairRequestId || paramRepairRequestId;
  const navigate = useNavigate();
  const isPanel = !!propRepairRequestId;
  const [repairRequest, setRepairRequest] = useState(null);
  const [equipments, setEquipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchSerials, setSearchSerials] = useState('');
  const [selectedEquipmentIds, setSelectedEquipmentIds] = useState([]);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [inlineError, setInlineError] = useState('');
  const [isTicketPanelOpen, setIsTicketPanelOpen] = useState(false);
  const pollTimeoutRef = useRef(null);

  const fetchApprovalDetails = useCallback(async () => {
    if (!repairRequestId) return;
    try {
      setLoading(true);
      const resp = await api.get(`/repairs/approvals/${repairRequestId}/`);
      setRepairRequest(resp.data.repair_request);
      setEquipments(resp.data.equipments);
      setSelectedEquipmentIds([]);
      setError('');
    } catch (err) {
      console.error(err);
      setError('Ошибка при загрузке деталей согласования');
    } finally {
      setLoading(false);
    }
  }, [repairRequestId]);

  useEffect(() => {
    if (!repairRequestId) return;
    fetchApprovalDetails();
  }, [repairRequestId, fetchApprovalDetails]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (inlineError) {
      const timer = setTimeout(() => setInlineError(''), 4000);
      return () => clearTimeout(timer);
    }
  }, [inlineError]);

  useEffect(() => {
    if (repairRequest?.project?.id) {
      try {
        localStorage.setItem('selected_project_id', repairRequest.project.id);
      } catch {
        /* ignore */
      }
    }
  }, [repairRequest]);

  useEffect(() => {
    return () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, []);

  const calculateTotalCost = (repairCodes) => {
    if (!repairCodes || !Array.isArray(repairCodes)) return 0;
    return repairCodes.reduce((sum, rc) => {
      const price = parseFloat(rc.price) || 0;
      return sum + price;
    }, 0);
  };

  const handleApprovalAction = async (equipmentId, action) => {
    try {
      setActionInProgress(true);
      await api.post('/repairs/assistant/response/', {
        equipment_id: equipmentId,
        action: action // 'confirm' or 'reject'
      });
      const wasLast = equipments.length === 1;
      setEquipments((prev) => prev.filter((eq) => eq.id !== equipmentId));
      setSelectedEquipmentIds((prev) => prev.filter((id) => id !== equipmentId));
      setSuccessMessage(`Оборудование ${action === 'confirm' ? 'согласовано' : 'отклонено'} успешно`);
      if (wasLast) {
        navigate('/approvals');
      }
    } catch (err) {
      console.error(`Ошибка при ${action === 'confirm' ? 'согласовании' : 'отказе'}:`, err);
      setError(`Ошибка при ${action === 'confirm' ? 'согласовании' : 'отказе'}`);
    } finally {
      setActionInProgress(false);
    }
  };

  const handleDownloadEstimates = async () => {
    try {
      setActionInProgress(true);
      const sendToTelegram = shouldSendToTelegram();
      if (sendToTelegram) {
        try {
          await api.post('/reports/generate_approval_estimates/', {
            repair_request_id: repairRequestId,
            send_to_telegram: true,
          }, {
            params: { send_to_telegram: true },
          });
          setSuccessMessage('Смета отправлена в Telegram');
          return;
        } catch (sendErr) {
          console.warn('Не удалось отправить смету в Telegram, пробуем скачать:', sendErr);
        }
      }

      const response = await api.post('/reports/generate_approval_estimates/', {
        repair_request_id: repairRequestId
      }, { responseType: 'blob' });

      const filename = `approval_estimates_${new Date().toISOString().slice(0,10)}.xlsx`;
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      setSuccessMessage('Смета согласования успешно выгружена');
    } catch (err) {
      console.error('Ошибка при скачивании/отправке сметы:', err);
      setError(shouldSendToTelegram() ? 'Не удалось отправить смету в Telegram' : 'Ошибка при скачивании сметы');
    } finally {
      setActionInProgress(false);
    }
  };

  const handleRemoveRepairCode = async (equipmentId, code, codeIndex) => {
    if (!code) return;
    const confirmed = window.confirm(`Удалить работу ${code.code} — ${code.name}?`);
    if (!confirmed) return;
    try {
      setActionInProgress(true);
      const { data } = await api.post(`/repairs/equipment/${equipmentId}/remove-repair-code/`, {
        code_id: code.id,
        code_index: codeIndex
      });
      setEquipments((prev) =>
        prev.map((item) => (item.id === equipmentId ? { ...item, repair_details: data.repair_details } : item))
      );
      setSuccessMessage('Код удалён из сметы');
    } catch (err) {
      console.error('Ошибка удаления ремонтного кода:', err);
      setInlineError('Не удалось удалить ремонтный код. Попробуйте ещё раз.');
    } finally {
      setActionInProgress(false);
    }
  };

  const pollTaskStatus = (taskId, attempt = 0) => {
    if (!taskId) {
      setActionInProgress(false);
      return;
    }
    const MAX_ATTEMPTS = 30;
    api.get(`/repairs/tasks/${taskId}/`)
      .then(async ({ data }) => {
        if (data.status === 'SUCCESS') {
          setSuccessMessage('Массовая операция завершена');
          pollTimeoutRef.current && clearTimeout(pollTimeoutRef.current);
          await fetchApprovalDetails();
          setSearchSerials('');
          setActionInProgress(false);
        } else if (data.status === 'FAILURE') {
          pollTimeoutRef.current && clearTimeout(pollTimeoutRef.current);
          setInlineError(data.error || 'Ошибка фоновой задачи');
          setActionInProgress(false);
        } else if (attempt < MAX_ATTEMPTS) {
          pollTimeoutRef.current = setTimeout(() => pollTaskStatus(taskId, attempt + 1), 2000);
        } else {
          setInlineError('Не удалось дождаться завершения фоновой задачи');
          setActionInProgress(false);
        }
      })
      .catch((err) => {
        console.error('Ошибка проверки статуса задачи:', err);
        setInlineError('Ошибка проверки статуса задачи');
        setActionInProgress(false);
      });
  };

  const triggerBulkAction = async (equipmentIds, action) => {
    if (!equipmentIds.length) return;
    try {
      setActionInProgress(true);
      const { data } = await api.post('/repairs/assistant/bulk-response/', {
        equipment_ids: equipmentIds,
        action,
      });
      setSuccessMessage(`Запущена обработка ${equipmentIds.length} позиций`);
      if (data?.task_id) {
        pollTaskStatus(data.task_id);
      } else {
        await fetchApprovalDetails();
        setActionInProgress(false);
      }
    } catch (err) {
      console.error('Ошибка запуска массовой операции:', err);
      setError('Ошибка запуска массовой операции');
      setActionInProgress(false);
    }
  };

  const handleApproveAll = async () => {
    const ids = equipments.map(eq => eq.id);
    await triggerBulkAction(ids, 'confirm');
  };

  const handleBatchAction = async (action) => {
    const ids = selectedEquipmentIds.filter((id) =>
      filteredEquipments.some((eq) => Number(eq.id) === Number(id))
    );
    if (ids.length === 0) return;
    await triggerBulkAction(ids, action);
  };

  const filteredEquipments = equipments.filter(eq => {
    if (!searchSerials.trim()) return true;
    const serials = searchSerials.toLowerCase().split(/\s+/).filter(s => s);
    return serials.some(serial => eq.serial_number.toLowerCase().includes(serial));
  });
  const selectedVisibleCount = selectedEquipmentIds.filter((id) =>
    filteredEquipments.some((eq) => Number(eq.id) === Number(id))
  ).length;

  const toggleEquipmentSelection = (equipmentId) => {
    setSelectedEquipmentIds((prev) => (
      prev.includes(equipmentId)
        ? prev.filter((id) => id !== equipmentId)
        : [...prev, equipmentId]
    ));
  };

  const toggleVisibleSelection = () => {
    const visibleIds = filteredEquipments.map((eq) => eq.id);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedEquipmentIds.includes(id));
    setSelectedEquipmentIds(allVisibleSelected ? [] : visibleIds);
  };

  const getEngineerName = (equipment) => {
    const logs = equipment?.repair_logs;
    if (!Array.isArray(logs) || logs.length === 0) return '';

    const relevantLog = logs.reduce((latest, log) => {
      if (!log || !log.action) return latest;
      const action = log.action.toLowerCase();
      const matchesEngineerAction =
        action.includes('запрос на согласование') ||
        action.includes('ожидает контроля качества') ||
        action.includes('возврат без ремонта');
      if (!matchesEngineerAction) return latest;

      const logTime = getLogTimestamp(log.timestamp);
      if (!latest) return log;
      const latestTime = getLogTimestamp(latest.timestamp);
      return logTime > latestTime ? log : latest;
    }, null);

    if (!relevantLog) return '';
    return relevantLog.performed_by_full_name || relevantLog.performed_by || '';
  };

  if (loading) return <Loading />;
  if (error) return <div className={`${commonStyles.error} ${styles.errorContainer}`}>
    <AlertCircle className={styles.errorIcon} />
    <p>{error}</p>
    <button className={styles.retryButton} onClick={fetchApprovalDetails}>Повторить</button>
  </div>;
  if (!repairRequest) return <div className={commonStyles.loading}>Нет данных</div>;

  const { customer, territory, project, created_at, request_number } = repairRequest;

  const containerClass = `${commonStyles.pageContainer} ${commonStyles.fadeIn}`;

  return (
    <div className={containerClass}>
      {successMessage && (
        <div className={styles.successNotification}>
          <CheckCircle className={styles.successIcon} />
          <span>{successMessage}</span>
        </div>
      )}
      {inlineError && (
        <div className={styles.errorNotification}>
          <AlertCircle className={styles.errorIcon} />
          <span>{inlineError}</span>
        </div>
      )}
      
      <div className={styles.pageHeader}>
        {!isPanel && (
          <button
            className={styles.backButton}
            onClick={() => navigate('/approvals')}
          >
            <ArrowLeft size={20} />
            <span>Назад к списку</span>
          </button>
        )}
        
        <div className={styles.headerContent}>
          <div className={styles.titleGroup}>
            <CheckCircle className={styles.titleIcon} />
            <h1 className={commonStyles.pageTitle}>Детали согласования</h1>
            <button
              className={styles.downloadButton}
              onClick={handleDownloadEstimates}
              title="Выгрузить смету в Excel"
              disabled={actionInProgress}
            >
              <Download size={16} />
              <span>Выгрузить смету</span>
            </button>
            <button
              type="button"
              className={styles.ticketButton}
              onClick={() => setIsTicketPanelOpen(true)}
            >
              <MessageSquare size={16} />
              <span>Тикет</span>
            </button>
          </div>
          
          <div className={styles.requestInfo}>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Номер заявки</span>
                <span className={styles.infoValue}>{request_number}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Заказчик</span>
                <span className={styles.infoValue}>{customer?.name || '-'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>ИНН</span>
                <span className={styles.infoValue}>{customer?.inn || '-'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Телефон</span>
                <span className={styles.infoValue}>{territory?.phone || '-'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Email</span>
                <span className={styles.infoValue}>{territory?.email || '-'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Проект</span>
                <span className={styles.infoValue}>{project?.name || '-'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Дата создания</span>
                <span className={styles.infoValue}>
                  {formatDateTime(created_at)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.mainContent}>
        <div className={styles.equipmentSection}>
          <div className={styles.equipmentHeader}>
            <h3>Оборудование для согласования ({filteredEquipments.length})</h3>
            <button
              className={`${commonStyles.button} ${styles.approveAllButton}`}
              onClick={handleApproveAll}
              disabled={equipments.length === 0 || actionInProgress}
            >
              <CheckCircle size={18} />
              <span>Согласовать всё</span>
            </button>
          </div>
          
          <div className={styles.searchContainer}>
            <div className={styles.searchInputWrapper}>
              <Search className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Поиск по серийным номерам (через пробел)"
                value={searchSerials}
                onChange={(e) => setSearchSerials(e.target.value)}
                className={styles.searchInput}
              />
              {searchSerials && (
                <button 
                  className={styles.clearSearchButton}
                  onClick={() => setSearchSerials('')}
                >
                  ×
                </button>
              )}
            </div>
            
            {searchSerials && (
              <div className={styles.searchResults}>
                Найдено: {filteredEquipments.length} из {equipments.length}
              </div>
            )}
            
            <div className={styles.filterActions}>
              <button
                className={`${commonStyles.button} ${styles.selectButton}`}
                onClick={toggleVisibleSelection}
                disabled={filteredEquipments.length === 0 || actionInProgress}
              >
                <span>
                  {filteredEquipments.length > 0 && filteredEquipments.every((eq) => selectedEquipmentIds.includes(eq.id))
                    ? 'Снять выбор'
                    : 'Выбрать видимые'}
                </span>
              </button>
              <button
                className={`${commonStyles.button} ${styles.approveButton}`}
                onClick={() => handleBatchAction('confirm')}
                disabled={selectedVisibleCount === 0 || actionInProgress}
              >
                <CheckCircle size={18} />
                <span>Согласовать выбранное{selectedVisibleCount ? ` (${selectedVisibleCount})` : ''}</span>
              </button>
              <button
                className={`${commonStyles.button} ${styles.rejectButton}`}
                onClick={() => handleBatchAction('reject')}
                disabled={selectedVisibleCount === 0 || actionInProgress}
              >
                <XCircle size={18} />
                <span>Отклонить выбранное{selectedVisibleCount ? ` (${selectedVisibleCount})` : ''}</span>
              </button>
            </div>
          </div>
          
          <div className={styles.equipmentList}>
            {filteredEquipments.length === 0 ? (
              <div className={styles.noEquipment}>
                {searchSerials ? 'Оборудование не найдено по указанным критериям' : 'Нет оборудования для согласования'}
              </div>
            ) : (
              filteredEquipments.map((eq) => (
                <div
                  key={eq.id}
                  className={`${styles.equipmentCard} ${selectedEquipmentIds.includes(eq.id) ? styles.equipmentCardSelected : ''}`}
                >
                  <div className={styles.equipmentCardHeader}>
                    <label className={styles.equipmentSelect}>
                      <input
                        type="checkbox"
                        checked={selectedEquipmentIds.includes(eq.id)}
                        onChange={() => toggleEquipmentSelection(eq.id)}
                      />
                      <span className={styles.equipmentInfo}>
                        <span className={styles.serialNumber}>{eq.serial_number}</span>
                        <span className={styles.model}>{eq.equipment_model}</span>
                      </span>
                    </label>
                    <div className={styles.engineerInfo}>
                      <span className={styles.engineerLabel}>Инженер</span>
                      <span className={styles.engineerValue}>{getEngineerName(eq) || '—'}</span>
                    </div>
                  </div>
                  
                  <div className={styles.codesSections}>
                    <div className={styles.codesColumn}>
                      <h4>Диагностические коды</h4>
                      {(eq.repair_details?.diagnostic_codes || []).length > 0 ? (
                        <ul className={styles.codesList}>
                          {(eq.repair_details?.diagnostic_codes || []).map((dc) => (
                            <li key={dc.id} className={styles.codeItem}>
                              <span className={styles.codeBadge}>{dc.code}</span>
                              <span className={styles.codeName}>{dc.name}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className={styles.noCodes}>Нет диагностических кодов</p>
                      )}
                    </div>
                    
                    <div className={styles.codesColumn}>
                      <h4>Ремонтные коды</h4>
                      {(eq.repair_details?.repair_codes || []).length > 0 ? (
                        <ul className={styles.codesList}>
                          {(eq.repair_details?.repair_codes || []).map((rc, rcIndex) => (
                            <li key={`${rc.id}-${rcIndex}`} className={styles.codeItem}>
                              <span className={styles.codeBadge}>{rc.code}</span>
                              <div className={styles.codeDetails}>
                                <span className={styles.codeName}>{rc.name}</span>
                                <span className={styles.priceTag}>{parseFloat(rc.price).toLocaleString()} ₽</span>
                              </div>
                              <button
                                type="button"
                                className={styles.removeCodeButton}
                                title="Удалить код из сметы"
                                onClick={() => handleRemoveRepairCode(eq.id, rc, rcIndex)}
                                disabled={actionInProgress}
                              >
                                <Trash2 size={16} />
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className={styles.noCodes}>Нет ремонтных кодов</p>
                      )}
                      {(eq.repair_details?.removed_repair_codes || []).some((item) => !item.notified) && (
                        <div className={styles.removedHint}>
                          <AlertCircle size={16} />
                          <span>
                            Удалено: {(eq.repair_details?.removed_repair_codes || [])
                              .filter((item) => !item.notified)
                              .map((item) => item.code)
                              .join(', ')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className={styles.equipmentCardFooter}>
                    <div className={styles.totalCost}>
                      <span>Общая стоимость:</span>
                      <span className={styles.costValue}>
                        {calculateTotalCost(eq.repair_details?.repair_codes).toLocaleString()} ₽
                      </span>
                    </div>
                    
                    <div className={styles.actionButtons}>
                      <button
                        className={`${commonStyles.button} ${styles.approveButton}`}
                        onClick={() => handleApprovalAction(eq.id, 'confirm')}
                        disabled={actionInProgress}
                      >
                        <CheckCircle size={18} />
                        <span>Согласовать</span>
                      </button>
                      <button
                        className={`${commonStyles.button} ${styles.rejectButton}`}
                        onClick={() => handleApprovalAction(eq.id, 'reject')}
                        disabled={actionInProgress}
                      >
                        <XCircle size={18} />
                        <span>Отклонить</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      <CreateClientTicketPanel
        isOpen={isTicketPanelOpen}
        repairRequest={repairRequest}
        onClose={() => setIsTicketPanelOpen(false)}
      />
    </div>
  );
});

ApprovalDetailPage.displayName = 'ApprovalDetailPage';

export default ApprovalDetailPage;
